/**
 * Boîte à outils — nombres rationnels exacts (pur).
 *
 * Les curseurs des outils avancent par pas décimaux (0,1 ; 0,5) : leurs
 * valeurs, et ce qu'on en tire par + − × ÷, sont des fractions. On les
 * calcule en entiers pour écrire « 10/3 » plutôt que « 3,333 ».
 */

import { formatNumber } from './format';

export interface ExactNumber {
  plain: string;
  tex: string;
  /** bouts pour la feuille de brouillon */
  atoms: string[];
  value: number;
}

export interface Frac {
  p: number;
  q: number;
}

function gcd(x: number, y: number): number {
  x = Math.abs(x);
  y = Math.abs(y);
  while (y) [x, y] = [y, x % y];
  return x || 1;
}

export function frac(p: number, q = 1): Frac {
  if (q === 0) throw new Error('division par zéro');
  if (q < 0) {
    p = -p;
    q = -q;
  }
  const g = gcd(p, q);
  const r = { p: p / g, q: q / g };
  return Object.is(r.p, -0) ? { p: 0, q: 1 } : r;
}

/** décimal d'un curseur (au plus 3 décimales) → fraction exacte */
export function fromDecimal(v: number): Frac {
  return frac(Math.round(v * 1000), 1000);
}

export const add = (a: Frac, b: Frac) => frac(a.p * b.q + b.p * a.q, a.q * b.q);
export const sub = (a: Frac, b: Frac) => frac(a.p * b.q - b.p * a.q, a.q * b.q);
export const mul = (a: Frac, b: Frac) => frac(a.p * b.p, a.q * b.q);
export const div = (a: Frac, b: Frac) => frac(a.p * b.q, a.q * b.p);
export const isZero = (a: Frac) => a.p === 0;
export const value = (a: Frac) => a.p / a.q;

/** p/q réduit : décimal s'il tombe juste (q = 2ⁱ5ʲ), sinon fraction. */
export function rational(p: number, q: number): ExactNumber {
  const f = frac(p, q);
  p = f.p;
  q = f.q;
  const v = p / q;
  let rest = q;
  let twos = 0;
  let fives = 0;
  while (rest % 2 === 0) {
    rest /= 2;
    twos++;
  }
  while (rest % 5 === 0) {
    rest /= 5;
    fives++;
  }
  const neg = p < 0;
  if (rest === 1) {
    const t = formatNumber(Math.abs(v), Math.max(twos, fives));
    return {
      plain: `${neg ? '−' : ''}${t}`,
      tex: `${neg ? '-' : ''}${t.replace(',', '{,}')}`,
      atoms: neg ? ['−', t] : [t],
      value: v,
    };
  }
  const n = Math.abs(p);
  return {
    plain: `${neg ? '−' : ''}${n}/${q}`,
    tex: `${neg ? '-' : ''}\\frac{${n}}{${q}}`,
    atoms: [...(neg ? ['−'] : []), String(n), '÷', String(q)],
    value: v,
  };
}

export function exact(f: Frac): ExactNumber {
  return rational(f.p, f.q);
}
